import { useCallback, useEffect, useState, type SyntheticEvent } from "react";

import { useT } from "../../../shared/i18n";
import {
  Badge,
  Button,
  controlClassName,
  Input,
  Skeleton,
} from "../../../shared/ui";
import { AdminListRow } from "../AdminListRow";
import { ErrorRetry } from "../ErrorRetry";
import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import {
  formatDateTime,
  isValidUserQuery,
  shortUserId,
  userLabel,
  USER_QUERY_MAX,
} from "./format";
import {
  USER_STATUSES,
  type UserListItem,
  type UserListQuery,
  type UsersApi,
} from "./usersApi";

type Status = "loading" | "ready" | "failed" | "denied";

/** Searchable, cursor-paginated master list of users. */
export function UserList({
  api,
  selectedId,
  hrefFor,
  onOpen,
}: {
  api: UsersApi;
  selectedId: string | null;
  hrefFor: (userId: string) => string;
  onOpen: (userId: string) => void;
}) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [draft, setDraft] = useState("");
  const [filter, setFilter] = useState<UserListQuery>({});
  const [invalid, setInvalid] = useState(false);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [piiRedacted, setPiiRedacted] = useState(false);

  const fetchPage = useCallback(
    (next: UserListQuery, after: string | null) => {
      api
        .list({ ...next, cursor: after })
        .then((page) => {
          setUsers((current) =>
            after === null ? page.items : [...current, ...page.items],
          );
          setCursor(page.next_cursor);
          setPiiRedacted(page.pii_redacted === true);
          setInvalid(false);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (isSessionExpired(error)) reportExpired();
          if (
            error instanceof OperatorRefused &&
            error.code.startsWith("users_invalid")
          ) {
            setInvalid(true);
            setStatus("ready");
            return;
          }
          setStatus(error instanceof OperatorDenied ? "denied" : "failed");
        });
    },
    [api, reportExpired],
  );

  const load = (next: UserListQuery, after: string | null) => {
    setStatus("loading");
    fetchPage(next, after);
  };

  useEffect(() => {
    fetchPage({}, null);
  }, [fetchPage]);

  const refilter = (next: UserListQuery) => {
    setFilter(next);
    load(next, null);
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!isValidUserQuery(draft)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    refilter({ ...filter, query: draft.trim() });
  };

  if (status === "denied") {
    return <PermissionDenied message={t("admin.denied.users")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <form
        onSubmit={submit}
        className="sticky top-0 z-10 flex flex-col gap-2 bg-canvas pb-2"
      >
        <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
          {t("admin.users.search.label")}
          <Input
            value={draft}
            placeholder={t("admin.users.search.placeholder")}
            aria-invalid={invalid}
            maxLength={USER_QUERY_MAX}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
        </label>
        <div className="flex items-end gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-footnote text-fg-muted">
            {t("admin.users.status.label")}
            <select
              className={controlClassName}
              value={filter.status ?? ""}
              onChange={(event) => {
                refilter({
                  ...filter,
                  status: event.target.value || undefined,
                });
              }}
            >
              <option value="">{t("admin.users.status.any")}</option>
              {USER_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="secondary">
            {t("admin.users.search.submit")}
          </Button>
        </div>
      </form>
      {invalid && (
        <p role="alert" className="text-footnote text-error">
          {t("admin.users.search.invalid")}
        </p>
      )}
      {piiRedacted && (
        <p className="text-caption text-fg-muted">
          {t("admin.users.piiHidden")}
        </p>
      )}
      {status === "failed" && (
        <ErrorRetry
          message={t("admin.users.failed")}
          onRetry={() => {
            load(filter, null);
          }}
        />
      )}
      {status === "loading" && users.length === 0 && (
        <div aria-busy="true">
          <Skeleton height={48} />
          <span className="sr-only">{t("admin.users.loading")}</span>
        </div>
      )}
      {status === "ready" && users.length === 0 && (
        <p className="text-body text-fg-muted">{t("admin.users.empty")}</p>
      )}
      {users.length > 0 && (
        <ul
          aria-label={t("admin.users.list.caption")}
          className="divide-y divide-line-subtle overflow-hidden rounded-xl bg-raised shadow-card"
        >
          {users.map((row) => (
            <li key={row.user_id}>
              <UserRow
                row={row}
                selected={row.user_id === selectedId}
                href={hrefFor(row.user_id)}
                onOpen={onOpen}
              />
            </li>
          ))}
        </ul>
      )}
      {cursor !== null && status !== "loading" && (
        <Button
          variant="secondary"
          onClick={() => {
            load(filter, cursor);
          }}
        >
          {t("admin.users.more")}
        </Button>
      )}
    </div>
  );
}

function UserRow({
  row,
  selected,
  href,
  onOpen,
}: {
  row: UserListItem;
  selected: boolean;
  href: string;
  onOpen: (userId: string) => void;
}) {
  const t = useT();
  const label = userLabel(row);
  const plan = row.current_plan_id ?? t("admin.users.noPlan");
  return (
    <AdminListRow
      href={href}
      selected={selected}
      onSelect={() => {
        onOpen(row.user_id);
      }}
    >
      <span className="flex min-w-0 items-center justify-between gap-2">
        <span
          className={
            label.primary === shortUserId(row.user_id)
              ? "truncate font-mono text-callout font-semibold text-fg"
              : "truncate text-callout font-semibold text-fg"
          }
        >
          {label.primary}
        </span>
        <Badge tone={row.status === "active" ? "success" : "warning"}>
          {row.status}
        </Badge>
      </span>
      {label.secondary !== null && (
        <span className="truncate font-mono text-caption text-fg-muted">
          {label.secondary}
        </span>
      )}
      <span className="truncate text-caption text-fg-muted">
        {`${plan} · ${String(row.requests_30d)} ${t("admin.users.meta.requests")} · ${formatDateTime(row.last_activity_at)}`}
      </span>
    </AdminListRow>
  );
}
